import { useState } from 'react'
import {
  Drawer, Stack, Group, Text, Badge, Button, Switch, Textarea, Alert, Loader,
} from '@mantine/core'
import { IconShieldOff, IconAlertTriangle } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatApiError } from '@/api/errors'
import {
  listOrgCapabilities, listManagerRestrictions, addManagerRestriction,
  removeManagerRestriction, RESTRICTION_ERROR_LABEL,
} from '@/api/org-restrictions'

/**
 * Alçada de um gestor numa Organização (spec 2026-07-29 §7).
 *
 * Modelo de **subtração**: toda capacidade vem concedida pelo papel de gestor, e o
 * Admin *tira*. Por isso cada linha nasce ligada — desligar é que exige ação, e exige
 * **motivo**, que fica na trilha de auditoria e é o que o gestor lê depois para
 * entender por que uma ação lhe é negada.
 *
 * Vive fora do `OrganizationManager` (já com 960 linhas) de propósito: é uma
 * responsabilidade própria, com estado e requisições próprias.
 */

interface Props {
  opened: boolean
  onClose: () => void
  orgId: number
  orgName: string
  userId: number
  userLabel: string
}

function erroLegivel(err: unknown): string {
  const bruto = formatApiError(err)
  for (const [code, msg] of Object.entries(RESTRICTION_ERROR_LABEL)) {
    if (bruto.includes(code)) return msg
  }
  return bruto
}

export function OrgCapabilityDrawer({
  opened, onClose, orgId, orgName, userId, userLabel,
}: Props) {
  const qc = useQueryClient()
  // Motivo em digitação, por capacidade — só existe enquanto o Admin está tirando.
  const [motivos, setMotivos] = useState<Record<string, string>>({})
  const [erro, setErro] = useState<string | null>(null)

  const { data: capabilities = [], isLoading: loadingCaps } = useQuery({
    queryKey: ['org-capabilities'],
    queryFn: listOrgCapabilities,
    enabled: opened,
  })

  const { data: restricoes = [], isLoading: loadingRestr } = useQuery({
    queryKey: ['manager-restrictions', orgId, userId],
    queryFn: () => listManagerRestrictions(orgId, userId),
    enabled: opened,
  })

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ['manager-restrictions', orgId, userId] })
    qc.invalidateQueries({ queryKey: ['org-restrictions', orgId] })
  }

  const tirar = useMutation({
    mutationFn: ({ capability, reason }: { capability: string; reason: string }) =>
      addManagerRestriction(orgId, userId, capability, reason),
    onSuccess: (_d, { capability }) => {
      setMotivos(m => ({ ...m, [capability]: '' }))
      setErro(null)
      invalidar()
    },
    onError: (err) => setErro(erroLegivel(err)),
  })

  const devolver = useMutation({
    mutationFn: (capability: string) =>
      removeManagerRestriction(orgId, userId, capability),
    onSuccess: () => { setErro(null); invalidar() },
    onError: (err) => setErro(erroLegivel(err)),
  })

  const restricaoDe = (key: string) => restricoes.find(r => r.capability === key)
  const carregando = loadingCaps || loadingRestr

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Group gap="xs">
          <IconShieldOff size={18} />
          <Text fw={600}>Alçada de {userLabel}</Text>
          <Badge variant="light">{orgName}</Badge>
        </Group>
      }
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Todo gestor recebe estas capacidades junto com o papel. Aqui você pode
          retirá-las — a mudança vale <strong>apenas nesta Organização</strong> e passa
          a valer na próxima ação dele.
        </Text>

        {erro && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>{erro}</Alert>
        )}

        {carregando && <Loader size="sm" />}

        {!carregando && capabilities.map(cap => {
          const restr = restricaoDe(cap.key)
          const concedida = !restr
          const motivo = motivos[cap.key] ?? ''
          const ocupado = tirar.isPending || devolver.isPending

          return (
            <Stack key={cap.key} gap={6} p="sm"
                   style={{ border: '1px solid var(--mantine-color-default-border)',
                            borderRadius: 8 }}>
              <Group justify="space-between" wrap="nowrap">
                <Stack gap={2}>
                  <Text fw={500} size="sm">{cap.label}</Text>
                  <Text size="xs" c="dimmed">{cap.description}</Text>
                </Stack>
                <Switch
                  checked={concedida}
                  disabled={ocupado}
                  aria-label={`Alçada: ${cap.label}`}
                  onChange={() => {
                    if (concedida) return   // desligar exige o motivo abaixo
                    devolver.mutate(cap.key)
                  }}
                />
              </Group>

              {concedida ? (
                <Stack gap={6}>
                  <Textarea
                    size="xs"
                    autosize
                    minRows={1}
                    label="Motivo para retirar"
                    description="Fica registrado na auditoria e é o que o gestor vê."
                    placeholder="Ex.: a Organização pediu que os textos sejam padronizados."
                    value={motivo}
                    onChange={(e) => {
                      // Capturar ANTES do updater: dentro dele o React já reciclou
                      // o evento e `currentTarget` é null.
                      const valor = e.currentTarget.value
                      setMotivos(m => ({ ...m, [cap.key]: valor }))
                    }}
                  />
                  <Group justify="flex-end">
                    <Button
                      size="xs"
                      color="red"
                      variant="light"
                      disabled={!motivo.trim() || ocupado}
                      loading={tirar.isPending}
                      onClick={() => tirar.mutate(
                        { capability: cap.key, reason: motivo.trim() })}
                    >
                      Retirar capacidade
                    </Button>
                  </Group>
                </Stack>
              ) : (
                <Alert color="orange" variant="light" p="xs">
                  <Text size="xs">
                    <strong>Retirada.</strong> {restr!.reason}
                  </Text>
                </Alert>
              )}
            </Stack>
          )
        })}
      </Stack>
    </Drawer>
  )
}
